import { StyleProps, useToken } from '@chakra-ui/react';

import { Admin } from '@/client/components/icons/individual/Admin';
import { BarChart } from '@/client/components/icons/individual/BarChart';
import { Create } from '@/client/components/icons/individual/Create';
import { Dashboard } from '@/client/components/icons/individual/Dashboard';
import { Explore } from '@/client/components/icons/individual/Explore';
import { Globe } from '@/client/components/icons/individual/Globe';
import { LaptopApps } from '@/client/components/icons/individual/LaptopApps';
import { Manager } from '@/client/components/icons/individual/Manager';
import { Workshop } from '@/client/components/icons/individual/Workshop';
import { admin } from '@/client/utils/routeLookup';

import { hexToRGBA } from '@/client/utils/hexToRGBA';
import { useAccessHelper } from '@/client/services/hooks/auth/useAccessHelper';
import { useAuthStore } from '@/client/services/state/authStore';
import { useCheckFeatureFlag } from '@/client/services/hooks/auth/useCheckFeatureFlag';
import { LEGACY_INTEGRATIONS_PATH } from '@/client/components/admin/integrations/legacyIntegrations';
import { useIsHubAdmin } from '@/client/services/hooks/hubs/useHubChildren';
import { useTranslation } from 'react-i18next';

export type NavChild = {
  name: string | null;
  route: string;
  enabled: boolean;
  isAngular: boolean;
};

export type NavLink = {
  name?: string | null;
  icon?: JSX.Element;
  route?: string;
  enabled: boolean;
  children?: null | NavChild[];
  isAngular?: boolean;
  divider?: boolean;
};

export const useSideNavigation = (): { navLinks: NavLink[]; parentLinkStyles: StyleProps } => {
  const { t } = useTranslation();
  const { authConfig } = useAuthStore();
  const { company, user } = authConfig;
  const [brandColor] = useToken('colors', ['brand.primary']);

  const { isManager, isGrader, allowance, isPartnerPrincipal } = useAccessHelper();
  const { isHubAdmin } = useIsHubAdmin();

  const workshopsMigrationEnabled = useCheckFeatureFlag('enable_workshops_migration');
  const integrationsMigrationEnabled = useCheckFeatureFlag('enable_integrations_migration');
  const dedicatedWorkshopsEnabled =
    company.feature_flags.enable_dedicated_workshops && !allowance.inPartner && !user.is_purchaser;

  const managerEnabled = (isManager() || isGrader()) && !allowance.inPartner;

  const assignmentsEnabled = allowance.assignmentsV2
    ? allowance.assignments || allowance.workflows
    : allowance.assignments;

  const createEnabled =
    !allowance.inPartner &&
    (allowance.content ||
      allowance.workshops ||
      allowance.assessments ||
      allowance.badges ||
      allowance.certificates ||
      allowance.surveys ||
      assignmentsEnabled ||
      allowance.journeys ||
      allowance.workflows);

  const adminEnabled =
    !allowance.inPartner && (allowance.users || allowance.theme || allowance.settings);

  const reportingEnabled = allowance.overview || allowance.reports || allowance.continuInsights;

  const navLinks: NavLink[] = [
    {
      name: t('sideNav.explore'),
      icon: <Explore />,
      route: '/explore',
      enabled: true,
      children: null,
      isAngular: false,
    },
    {
      name: t('sideNav.dashboard'),
      icon: <Dashboard />,
      route: '/dashboard',
      enabled: true,
      children: null,
      isAngular: false,
    },
    {
      name: t('sideNav.workshops'),
      icon: <Workshop />,
      enabled: dedicatedWorkshopsEnabled,
      route: '/workshops',
      children: null,
      isAngular: false,
    },
    {
      divider: true,
      enabled: true,
    },
    {
      name: t('sideNav.managers'),
      icon: <Manager />,
      route: '/manager',
      enabled: managerEnabled,
      children: null,
      isAngular: false,
    },
    {
      name: t('sideNav.create'),
      icon: <Create />,
      enabled: createEnabled,
      children: [
        {
          // Unified React overview at `/admin/content` replaces the
          // legacy Angular `/pvt/#/overview/content` surface. All
          // content types — articles, videos, files, tracks, journeys,
          // scorm, imported content — are reachable from here;
          // per-action permission checks still apply per row.
          name: t('sideNav.create.content'),
          route: '/admin/content',
          // assessmentsMigration folds assessments into this overview — an
          // assessments-only creator needs this entry once the Angular one hides.
          enabled: allowance.content || allowance.assessmentsMigration,
          isAngular: false,
        },
        {
          name: t('sideNav.create.assessments'),
          route: '/pvt/#/overview/assessments',
          // Migration folds assessments into the React Content overview —
          // hide the Angular entry so there's one front door.
          enabled: allowance.assessments && !allowance.assessmentsMigration,
          isAngular: true,
        },
        {
          name: t('sideNav.create.workshops'),
          route: workshopsMigrationEnabled ? '/admin/workshops' : '/pvt/#/overview/workshops',
          enabled: allowance.workshops,
          isAngular: !workshopsMigrationEnabled,
        },
        {
          name: t('sideNav.create.badges'),
          route: '/admin/badges',
          enabled: allowance.badges,
          isAngular: false,
        },
        {
          name: t('sideNav.create.certificates'),
          route: '/admin/certificates',
          enabled: allowance.certificates,
          isAngular: false,
        },
        {
          name: t('sideNav.create.surveys'),
          route: '/admin/surveys',
          enabled: allowance.surveys,
          isAngular: false,
        },
        {
          name: t('sideNav.create.assignments'),
          route: allowance.assignmentsV2
            ? '/admin/assignments/overview?type=all'
            : '/pvt/#/overview/assignments',
          enabled: allowance.assignmentsV2
            ? allowance.assignments || allowance.workflows
            : allowance.assignments,
          isAngular: !allowance.assignmentsV2,
        },
        {
          name: t('sideNav.create.workflows'),
          route: '/pvt/#/overview/workflows',
          enabled: allowance.workflows && !allowance.assignmentsV2,
          isAngular: true,
        },
      ],
    },
    {
      name: t('sideNav.admin'),
      icon: <Admin />,
      enabled: adminEnabled,
      children: [
        {
          name: t('sideNav.admin.users'),
          route: '/admin/users',
          enabled: allowance.users,
          isAngular: false,
        },
        {
          name: t('sideNav.admin.theme'),
          route: '/admin/theme',
          enabled: allowance.theme,
          isAngular: true,
        },
        {
          name: t('sideNav.admin.integrations'),
          route: integrationsMigrationEnabled ? '/admin/integrations' : LEGACY_INTEGRATIONS_PATH,
          enabled: allowance.settings,
          isAngular: !integrationsMigrationEnabled,
        },
        {
          name: t('sideNav.admin.adminUtilities'),
          route: '/admin/utilities',
          enabled: true,
          isAngular: false,
        },
        {
          name: t('aiAgent.experts.nav.title'),
          route: '/admin/eddy',
          enabled: true,
          isAngular: false,
        },
      ],
    },
    {
      name: t('sideNav.reporting'),
      icon: <BarChart />,
      enabled: reportingEnabled,
      isAngular: false,
      children: [
        {
          name: t('sideNav.reporting.overview'),
          route: admin(),
          enabled: allowance.overview,
          isAngular: true,
        },
        {
          name: t('sideNav.reporting.reports'),
          route: '/admin/reports',
          enabled: allowance.reports,
          isAngular: false,
        },
        {
          name: t('sideNav.reporting.insights'),
          route: '/admin/insights',
          enabled: allowance.continuInsights,
          isAngular: false,
        },
      ],
    },
    {
      name: t('sideNav.academies'),
      icon: <LaptopApps />,
      route: '/admin/academies',
      enabled: allowance.academies && !allowance.inPartner,
      isAngular: false,
      children: null,
    },
    {
      name: t('sideNav.hubs'),
      icon: <Globe />,
      route: '/admin/hubs',
      // allowance.hubs is flag+role; isHubAdmin is the Hub.admins probe —
      // membership isn't in the auth payload, so both are required.
      enabled: allowance.hubs && isHubAdmin && !allowance.inPartner,
      isAngular: false,
      children: null,
    },
    {
      name: t('overview.label.partnerManager'),
      icon: <Manager />,
      route: '/extend/manager',
      enabled: allowance.inPartner && isPartnerPrincipal(),
      isAngular: false,
      children: null,
    },
  ];

  const parentLinkStyles = {
    alignItems: 'center',
    width: 'full',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 500,
    color: 'neutral.1500',
    paddingY: 3,
    paddingX: 4,
    _hover: {
      cursor: 'pointer',
      backgroundColor: hexToRGBA(brandColor, 0.1),
      color: 'neutral.1500',
      fontWeight: 600,
    },
  };

  return { navLinks, parentLinkStyles };
};
